import { useState, useEffect, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Plus, Wrench, Check, Loader2 } from "lucide-react";

// Diálogo para vincular una plantilla (maestra/manual) a un servicio SIN salir de Plantillas.
// Dos modos: asignar un servicio existente o crear uno nuevo, quedando asociado a la plantilla.
export const AsignarServicioDialog = ({ open, plantillaId, plantillaNombre, onClose, onDone }) => {
  const [modo, setModo] = useState("existente"); // "existente" | "nuevo"
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (open) { setModo("existente"); setQ(""); setResultados([]); setNombreNuevo(""); }
  }, [open, plantillaId]);

  const buscar = useCallback(async () => {
    setBuscando(true);
    try {
      const { data } = await api.get("/servicios", { params: q.trim() ? { q: q.trim(), limit: 30 } : { limit: 30 } });
      setResultados(data);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setBuscando(false); }
  }, [q]);

  useEffect(() => {
    if (!open || modo !== "existente") return;
    const t = setTimeout(buscar, 300);
    return () => clearTimeout(t);
  }, [open, modo, buscar]);

  const asignarExistente = async (s) => {
    const yaAsociada = (s.plantillas_asociadas || []).includes(plantillaId) || s.plantilla?.id === plantillaId;
    if (yaAsociada) { toast.info("Esa plantilla ya está asociada a este servicio"); return; }
    setGuardando(true);
    try {
      const nuevas = Array.from(new Set([...(s.plantillas_asociadas || []), plantillaId]));
      await api.patch(`/servicios/${s.id}`, { plantillas_asociadas: nuevas });
      toast.success(`Plantilla vinculada al servicio "${s.nombre}"`);
      onDone?.();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  const crearNuevo = async () => {
    const nombre = nombreNuevo.trim();
    if (!nombre) { toast.error("Ingresá un nombre para el servicio"); return; }
    setGuardando(true);
    try {
      await api.post("/servicios", { nombre, activo: true, plantillas_asociadas: [plantillaId] });
      toast.success(`Servicio "${nombre}" creado y vinculado`);
      onDone?.();
    } catch (e) {
      // 409 = nombre duplicado: avisar para que use "asignar existente".
      if (e?.response?.status === 409) {
        toast.error("Ya existe un servicio con ese nombre. Usá \"Asignar existente\" para vincularlo.");
        setModo("existente"); setQ(nombre);
      } else toast.error(errorMsg(e));
    }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="asignar-servicio-dialog" className="bg-white max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench size={17} /> Asignar servicio</DialogTitle>
          <DialogDescription className="truncate">Plantilla: <b>{plantillaNombre}</b></DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 p-0.5 bg-slate-100 rounded-md">
          <button data-testid="asignar-servicio-modo-existente" onClick={() => setModo("existente")}
            className={`flex-1 text-sm font-medium py-1.5 rounded-sm transition-colors ${modo === "existente" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
            Asignar existente
          </button>
          <button data-testid="asignar-servicio-modo-nuevo" onClick={() => setModo("nuevo")}
            className={`flex-1 text-sm font-medium py-1.5 rounded-sm transition-colors ${modo === "nuevo" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
            Crear nuevo
          </button>
        </div>

        {modo === "existente" ? (
          <div className="flex-1 min-h-0 flex flex-col gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="asignar-servicio-buscar" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar servicio…" className="pl-8 h-9" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto border border-border rounded-sm divide-y divide-slate-100">
              {buscando && <p className="p-4 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Buscando…</p>}
              {!buscando && resultados.length === 0 && (
                <p data-testid="asignar-servicio-sin-resultados" className="p-4 text-center text-sm text-muted-foreground">
                  No hay servicios{q ? " para esa búsqueda" : ""}. Probá "Crear nuevo".
                </p>
              )}
              {!buscando && resultados.map((s) => {
                const yaAsociada = (s.plantillas_asociadas || []).includes(plantillaId) || s.plantilla?.id === plantillaId;
                return (
                  <button key={s.id} data-testid={`asignar-servicio-item-${s.id}`} disabled={guardando}
                    onClick={() => asignarExistente(s)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-indigo-50/60 disabled:opacity-50">
                    <span className="min-w-0">
                      <span className="text-sm font-medium text-slate-800 block truncate">{s.nombre}</span>
                      {(s.plantillas?.length > 0) && (
                        <span className="text-[11px] text-muted-foreground">{s.plantillas.length} plantilla(s) asociada(s)</span>
                      )}
                    </span>
                    {yaAsociada
                      ? <span className="text-[11px] text-teal-700 font-semibold flex items-center gap-0.5 shrink-0"><Check size={12} /> Asociada</span>
                      : <span className="text-[11px] text-indigo-600 font-semibold shrink-0">Asignar</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Nombre del servicio nuevo</label>
            <Input data-testid="asignar-servicio-nombre-nuevo" value={nombreNuevo} autoFocus
              onChange={(e) => setNombreNuevo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") crearNuevo(); }}
              placeholder="Ej: Cambio de aceite" className="h-9" />
            <p className="text-xs text-muted-foreground">Se creará el servicio y quedará vinculado a esta plantilla automáticamente.</p>
            <Button data-testid="asignar-servicio-crear-btn" onClick={crearNuevo} disabled={guardando || !nombreNuevo.trim()}
              className="w-full">
              {guardando ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Plus size={15} className="mr-1" />}
              Crear y vincular
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
